import {
  getVideoDurationAsync,
  getVideoMetadataAsync,
  getImageMetadataAsync,
} from "../utils/videoDuration";
import {
  VIDEO_EDITOR_DEFAULT_COMPOSITION,
  VIDEO_EDITOR_TRACK_TYPE,
} from "../features/videoEditor/constants";
import { LaunchError } from "../utils/errors";
import { startAddContentsToList, startBumpAndUpdateContentInList } from "./user";
import { renewToken } from "./auth";
import types from "../types";
import { toExportEdlPayload } from "../features/videoEditor/toExportEdlPayload";
import { exportVideoEditorProjectRequest } from "../services/videoEditorExport";
import {
  createInitialEdlFromVideo,
  getEdlDuration,
} from "../features/videoEditor/projectSchema";
import { applySourceSizesToEdl } from "../features/videoEditor/clipProperties";
import {
  applySubtitlesSidecarToEdl,
  collectExportSubtitles,
  edlHasSubtitleClips,
} from "../features/videoEditor/subtitleEdl";
import {
  createClipForTrack,
  getDefaultTrackTypeForAsset,
} from "../reducers/videoEditorReducer";
import {
  startHydrateCreativesVideoEdited,
  startSaveCreativesVideoEdited,
} from "./videoEditorProject";

export const initVideoEditorProject = ({ edl }) => ({
  type: types.VIDEO_EDITOR_INIT_PROJECT,
  payload: { edl },
});

export const resetVideoEditorProject = () => ({
  type: types.VIDEO_EDITOR_RESET,
});

export const setVideoEditorPlayhead = ({ playhead }) => ({
  type: types.VIDEO_EDITOR_SET_PLAYHEAD,
  payload: { playhead },
});

export const setVideoEditorSelectedClip = ({ clipId }) => ({
  type: types.VIDEO_EDITOR_SET_SELECTED_CLIP,
  payload: { clipId },
});

export const setVideoEditorSelection = ({
  clipId,
  clipIds,
  event,
  mode,
  preserveMulti = false,
} = {}) => ({
  type: types.VIDEO_EDITOR_SET_SELECTION,
  payload: { clipId, clipIds, event, mode, preserveMulti },
});

export const commitVideoEditorClipUpdate = ({ clipId, updates }) => ({
  type: types.VIDEO_EDITOR_COMMIT_CLIP_UPDATE,
  payload: { clipId, updates },
});

export const commitVideoEditorClipUpdates = ({ updates }) => ({
  type: types.VIDEO_EDITOR_COMMIT_CLIP_UPDATES,
  payload: { updates },
});

export const splitVideoEditorClipAtPlayhead = ({ clipId }) => ({
  type: types.VIDEO_EDITOR_SPLIT_CLIP_AT_PLAYHEAD,
  payload: { clipId },
});

export const addVideoEditorClip = ({ clip }) => ({
  type: types.VIDEO_EDITOR_ADD_CLIP,
  payload: { clip },
});

export const removeVideoEditorClip = ({ clipId }) => ({
  type: types.VIDEO_EDITOR_REMOVE_CLIP,
  payload: { clipId },
});

export const removeVideoEditorClips = ({ clipIds }) => ({
  type: types.VIDEO_EDITOR_REMOVE_CLIPS,
  payload: { clipIds },
});

export const setVideoEditorExporting = ({ isExporting }) => ({
  type: types.VIDEO_EDITOR_SET_EXPORTING,
  payload: { isExporting },
});

export const markVideoEditorSaved = () => ({
  type: types.VIDEO_EDITOR_MARK_SAVED,
});

export const applyVideoEditorEdl = ({ edl }) => ({
  type: types.VIDEO_EDITOR_APPLY_EDL,
  payload: { edl },
});

export const startInitVideoEditorFromSource =
  ({
    sourceUrl,
    subtitles = null,
    sourceAssetID,
    sourceName,
    compositionWidth,
    compositionHeight,
  }) =>
  async (dispatch) => {
    try {
      const playerUrl = String(sourceUrl || "").trim();
      const persisted = await dispatch(
        startHydrateCreativesVideoEdited({
          sourceUrl: playerUrl,
          sourceAssetID,
          sourceName,
        })
      );

      let edl = persisted?.edl || null;
      const metadata = await getVideoMetadataAsync({ url: playerUrl });
      const sourceWidth = metadata?.width;
      const sourceHeight = metadata?.height;

      if (!edl) {
        const duration =
          metadata?.duration || (await getVideoDurationAsync({ url: playerUrl }));
        edl = createInitialEdlFromVideo({
          sourceUrl: playerUrl,
          sourceAssetID,
          sourceName,
          duration: duration || 10,
          compositionWidth:
            sourceWidth ||
            compositionWidth ||
            VIDEO_EDITOR_DEFAULT_COMPOSITION.width,
          compositionHeight:
            sourceHeight ||
            compositionHeight ||
            VIDEO_EDITOR_DEFAULT_COMPOSITION.height,
          sourceWidth,
          sourceHeight,
        });
      } else {
        const sizesBySourceUrl = {};
        if (sourceWidth > 0 && sourceHeight > 0) {
          sizesBySourceUrl[playerUrl] = {
            width: sourceWidth,
            height: sourceHeight,
          };
        }
        const extraUrls = Object.values(edl.clips?.byId || {})
          .filter(
            (clip) =>
              clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO &&
              clip.sourceUrl &&
              clip.sourceUrl !== playerUrl
          )
          .map((clip) => clip.sourceUrl);
        await Promise.all(
          extraUrls.map(async (url) => {
            const extraMetadata = await getVideoMetadataAsync({ url });
            if (extraMetadata?.width > 0 && extraMetadata?.height > 0) {
              sizesBySourceUrl[url] = {
                width: extraMetadata.width,
                height: extraMetadata.height,
              };
            }
          })
        );
        edl = applySourceSizesToEdl({
          edl,
          sizesBySourceUrl,
          compositionSourceUrl: playerUrl,
        });
      }

      const sidecar =
        subtitles?.segments?.length > 0
          ? subtitles
          : persisted?.subtitles?.segments?.length > 0
            ? persisted.subtitles
            : null;

      if (sidecar && !edlHasSubtitleClips(edl)) {
        edl = applySubtitlesSidecarToEdl({
          edl,
          subtitles: sidecar,
        });
      }

      dispatch(initVideoEditorProject({ edl }));
      return edl;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startInitVideoEditorFromSource",
        })
      );
      return null;
    }
  };

export const startAddMediaClipToVideoEditor =
  ({ sourceUrl, type, label, duration, position }) =>
  async (dispatch, getState) => {
    const edl = getState()?.videoEditor?.edl;
    const playhead = edl?.playhead || 0;
    const clipType = getDefaultTrackTypeForAsset({ type });

    let resolvedDuration = duration;
    let sourceWidth;
    let sourceHeight;
    if (clipType === VIDEO_EDITOR_TRACK_TYPE.IMAGE) {
      resolvedDuration = 5;
      const metadata = await getImageMetadataAsync({ url: sourceUrl });
      sourceWidth = metadata?.width;
      sourceHeight = metadata?.height;
    }
    if (clipType === VIDEO_EDITOR_TRACK_TYPE.VIDEO) {
      const metadata = await getVideoMetadataAsync({ url: sourceUrl });
      sourceWidth = metadata?.width;
      sourceHeight = metadata?.height;
      resolvedDuration =
        resolvedDuration || metadata?.duration || 5;
    }
    if (clipType === VIDEO_EDITOR_TRACK_TYPE.AUDIO && !resolvedDuration) {
      resolvedDuration = (await getVideoDurationAsync({ url: sourceUrl })) || 5;
    }

    const clip = createClipForTrack({
      type: clipType,
      sourceUrl,
      label: label || clipType,
      duration: resolvedDuration || 5,
      sourceDuration:
        clipType === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
        clipType === VIDEO_EDITOR_TRACK_TYPE.AUDIO
          ? resolvedDuration || 5
          : undefined,
      sourceWidth,
      sourceHeight,
      compositionWidth: edl?.compositionWidth,
      compositionHeight: edl?.compositionHeight,
      position: position ?? playhead,
    });

    dispatch(addVideoEditorClip({ clip }));
    return clip;
  };

const persistVideoEditorProjectSettings = async ({
  dispatch,
  sourceUrl,
  url,
  subtitles,
  edl,
}) => {
  const cutsOnlyUrl = String(sourceUrl || url || "").trim();
  const finalUrl = String(url || cutsOnlyUrl).trim();
  if (!cutsOnlyUrl) {
    return;
  }

  await dispatch(
    startSaveCreativesVideoEdited({
      sourceUrl: cutsOnlyUrl,
      url: finalUrl,
      subtitles,
      edl,
    })
  );
};

export const startExportVideoEditorProject =
  ({
    pathStorage,
    sourceName = "edited-video",
    mode = "new",
    sourceAssetID = "",
    persistToContentList = true,
  }) =>
  async (dispatch, getState) => {
    try {
      const edl = getState()?.videoEditor?.edl;
      if (!edl) return { ok: false };

      const exportEdl = toExportEdlPayload({ edl });
      if (!exportEdl?.clips?.length) {
        return { ok: false };
      }

      dispatch(setVideoEditorExporting({ isExporting: true }));

      const token = await dispatch(renewToken());
      const response = await exportVideoEditorProjectRequest({
        token,
        path: pathStorage,
        edl: exportEdl,
      });

      if (!response?.ok || !response?.data?.url) {
        dispatch(setVideoEditorExporting({ isExporting: false }));
        return { ok: false };
      }

      const uploadedUrl = response.data.url;
      const sourceUrl = String(response.data.sourceUrl || uploadedUrl).trim();
      const duration = response.data.duration || getEdlDuration({ edl });
      const fileName = `${sourceName}-edited.mp4`;
      const subtitles = collectExportSubtitles({ edl });
      const persistToGallery = persistToContentList !== false;
      const saveMode =
        mode === "replace" && (sourceAssetID || !persistToGallery)
          ? "replace"
          : "new";

      await persistVideoEditorProjectSettings({
        dispatch,
        sourceUrl,
        url: uploadedUrl,
        subtitles,
        edl,
      });

      if (!persistToGallery) {
        dispatch(setVideoEditorExporting({ isExporting: false }));
        dispatch(markVideoEditorSaved());
        return {
          ok: true,
          url: uploadedUrl,
          sourceUrl,
          mode: saveMode,
          subtitles: subtitles || null,
          duration,
          item: {
            url: uploadedUrl,
            sourceUrl,
            subtitles: subtitles || null,
            duration,
            type: "video",
            name: fileName.replace(/\.[^.]+$/, ""),
          },
        };
      }

      if (saveMode === "replace") {
        const bumped = await dispatch(
          startBumpAndUpdateContentInList({
            id: sourceAssetID,
            url: uploadedUrl,
            sourceUrl,
            subtitles: subtitles || null,
            duration,
          })
        );

        dispatch(setVideoEditorExporting({ isExporting: false }));
        dispatch(markVideoEditorSaved());

        if (!bumped) {
          return { ok: false };
        }

        return {
          ok: true,
          url: uploadedUrl,
          sourceUrl,
          mode: saveMode,
          item: {
            id: sourceAssetID,
            url: uploadedUrl,
            sourceUrl,
            subtitles: subtitles || null,
            duration,
            type: "video",
            name: fileName.replace(/\.[^.]+$/, ""),
            ...bumped,
          },
        };
      }

      const saved = await dispatch(
        startAddContentsToList([
          {
            url: uploadedUrl,
            type: "video",
            name: fileName.replace(/\.[^.]+$/, ""),
            duration,
            ...(sourceUrl ? { sourceUrl } : {}),
            ...(subtitles ? { subtitles } : {}),
          },
        ])
      );

      dispatch(setVideoEditorExporting({ isExporting: false }));
      dispatch(markVideoEditorSaved());

      const savedEntry = saved ? Object.values(saved)[0] : null;
      return {
        ok: true,
        url: uploadedUrl,
        sourceUrl,
        mode: saveMode,
        item: savedEntry,
      };
    } catch (error) {
      dispatch(setVideoEditorExporting({ isExporting: false }));
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startExportVideoEditorProject",
        })
      );
      return { ok: false };
    }
  };
